/**
 * GoalFormModal - Add/Edit a Domain, Program, or Goal/Target node.
 * Mirrors EditClientModal (react-hook-form + shared Modal).
 */

import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import type { GoalNode, GoalLevel, GoalStatus } from '@upskill/shared';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { useCreateGoal, useUpdateGoal } from '../../hooks/useGoals';
import { extractApiError } from '../../utils/apiError';
import { media } from '../../styles/media';
import { LEVEL_LABELS, STATUS_OPTIONS } from './statusMeta';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[4]};

  ${media.tablet} {
    grid-template-columns: 1fr;
  }
`;

const ContextLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgrounds.page};
  border-radius: ${({ theme }) => theme.borderRadius.md};

  strong {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const FormError = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.status.error.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

interface GoalFormValues {
  name: string;
  status: GoalStatus;
  shortTermNote: string;
  dateInitiated: string;
  dateMet: string;
}

interface GoalFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  /** Editing an existing node. Omit/null when creating. */
  goal?: GoalNode | null;
  /** Creating a child under this node; null/undefined creates a root Domain. */
  parentNode?: GoalNode | null;
  /** The level being created (ignored in edit mode). */
  createLevel?: GoalLevel;
  /** Overlay z-index — raise this when opened on top of another high-z overlay (e.g. a drawer). */
  zIndex?: number;
}

export function GoalFormModal({
  isOpen,
  onClose,
  clientId,
  goal,
  parentNode,
  createLevel,
  zIndex,
}: GoalFormModalProps) {
  const isEdit = !!goal;
  const createGoal = useCreateGoal(clientId);
  const updateGoal = useUpdateGoal(clientId);
  const mutation = isEdit ? updateGoal : createGoal;

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<GoalFormValues>();

  useEffect(() => {
    if (!isOpen) return;
    reset({
      name: goal?.name ?? '',
      status: goal?.status ?? 'ACTIVE',
      shortTermNote: goal?.shortTermNote ?? '',
      dateInitiated: goal?.dateInitiated ? goal.dateInitiated.slice(0, 10) : '',
      dateMet: goal?.dateMet ? goal.dateMet.slice(0, 10) : '',
    });
    mutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, goal]);

  const level = isEdit ? goal!.level : (createLevel ?? 'DOMAIN');

  const handleClose = () => {
    mutation.reset();
    onClose();
  };

  const onSubmit = (values: GoalFormValues) => {
    const commonDto = {
      name: values.name.trim(),
      status: values.status,
      shortTermNote: values.shortTermNote.trim() || undefined,
      dateInitiated: values.dateInitiated || undefined,
      dateMet: values.dateMet || undefined,
    };

    if (isEdit && goal) {
      updateGoal.mutate({ goalId: goal.id, dto: commonDto }, { onSuccess: handleClose });
    } else {
      createGoal.mutate(
        { level, parentGoalId: parentNode?.id, ...commonDto },
        { onSuccess: handleClose },
      );
    }
  };

  const title = isEdit ? `Edit ${LEVEL_LABELS[level]}` : `Add ${LEVEL_LABELS[level]}`;
  const errorMessage = mutation.isError
    ? extractApiError(mutation.error, 'Something went wrong. Please try again.')
    : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      size="md"
      zIndex={zIndex}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary-blue"
            onClick={handleSubmit(onSubmit)}
            isLoading={mutation.isPending}
          >
            {isEdit ? 'Save Changes' : 'Create'}
          </Button>
        </>
      }
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        {errorMessage && <FormError>{errorMessage}</FormError>}

        {!isEdit && (
          <ContextLabel>
            {parentNode ? (
              <>
                Adding under <strong>{parentNode.name}</strong> ({LEVEL_LABELS[parentNode.level]})
              </>
            ) : (
              <>Adding a new root Domain</>
            )}
          </ContextLabel>
        )}

        <Input
          {...register('name', { required: `${LEVEL_LABELS[level]} name is required` })}
          label={`${LEVEL_LABELS[level]} Name`}
          placeholder={`e.g. ${level === 'DOMAIN' ? 'Communication' : level === 'PROGRAM' ? 'Requesting' : 'Mands for preferred items'}`}
          error={errors.name?.message}
          required
          fullWidth
        />

        <FieldRow>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select label="Status" options={STATUS_OPTIONS} value={field.value} onChange={field.onChange} fullWidth />
            )}
          />
          <Input {...register('shortTermNote')} label="Short-term note (optional)" placeholder="e.g. Short-term goal" fullWidth />
        </FieldRow>

        <FieldRow>
          <Input {...register('dateInitiated')} label="Date Initiated" type="date" fullWidth />
          <Input {...register('dateMet')} label="Date Met" type="date" fullWidth />
        </FieldRow>
      </Form>
    </Modal>
  );
}

export default GoalFormModal;
